import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as priorityCampaignActions from '@redux/modules/priorityCampaign'

import { launchModal } from '@domain-group/javascript-utils'

import defaultFormState, { requiredFields } from '@models/priorityCampaign'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams, ModuleState } from '@redux/modules/types'
import type { PriorityCampaignModel, PriorityCampaignRequestOptions, UserModel } from '@models/types'

import PageContext from '@contexts/pageContext'

type CreatePriorityCampaignParams = {
  dispatch: AppDispatch,
  priorityCampaignParams: Partial<PriorityCampaignModel>,
  requestOptions?: PriorityCampaignRequestOptions,
}

const createPriorityCampaign = (params: CreatePriorityCampaignParams) => {
  const { dispatch, priorityCampaignParams, requestOptions } = params
  const { createPriorityCampaign: createFn } = priorityCampaignActions

  return dispatch(createFn(priorityCampaignParams, requestOptions))
}

type DeletePriorityCampaignParams = {
  dispatch: AppDispatch,
  priorityCampaign: DeleteParams<PriorityCampaignModel>,
}

const deletePriorityCampaign = (params: DeletePriorityCampaignParams) => {
  const { dispatch, priorityCampaign } = params
  const { deletePriorityCampaign: deleteFn } = priorityCampaignActions

  return dispatch(deleteFn(priorityCampaign))
}

type CustomFormOptions = {
  customDeps?: string[] | boolean[],
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function usePriorityCampaignForm(
  priorityCampaign: Partial<PriorityCampaignModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const {
    customDeps = [],
    customRequiredFields = [],
    validateOn,
  } = options || {}

  const priorityCampaignForm = useForm(
    defaultFormState,
    { entity: priorityCampaign, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [...customDeps, priorityCampaign.id, priorityCampaign.cache_key],
  )

  return {
    ...priorityCampaignForm,
  }
}

export const useRelations = (priorityCampaign: Partial<PriorityCampaignModel> = {}) => {
  // @ts-expect-error TS2339
  const { related_entity_id } = priorityCampaign

  // @ts-expect-error TS2339
  const { relatedEntities } = useSelector(reduxState => reduxState.entities)

  const relatedEntity = related_entity_id && relatedEntities[related_entity_id] ? relatedEntities[related_entity_id] : {}

  return {
    relatedEntity,
  }
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: PriorityCampaignRequestOptions,
}

function usePriorityCampaign(initEntity: Partial<PriorityCampaignModel> = {}, options: Options = {}) {
  const { performHttpRequests, requestOptions } = options

  // @ts-expect-error TS2322
  const { entity: priorityCampaign }: { entity: PriorityCampaignModel} = useLatestEntity(initEntity, 'priorityCampaigns')
  const { id } = priorityCampaign

  const dispatch = useDispatch()

  const { relatedEntity } = useRelations(priorityCampaign)

  useReduxAction(
    'priorityCampaigns',
    'loadPriorityCampaign',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const {
    // @ts-expect-error TS2339
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.priorityCampaigns)

  const { callbacks } = useContext(PageContext)

  return {
    callbacks: {
      createPriorityCampaign: (
        priorityCampaignParams: Partial<PriorityCampaignModel>,
        entityOptions?: PriorityCampaignRequestOptions,
      ) => (
        createPriorityCampaign({ priorityCampaignParams, dispatch, requestOptions: entityOptions })
      ),
      deletePriorityCampaign: () => deletePriorityCampaign({ dispatch, priorityCampaign }),
      manageUserPriorityCampaigns: () => launchModal({
        callbacks,
        modalKey: 'ManageUserPriorityCampaignsModal',
        payload: { priorityCampaign },
      }),
    },
    creating,
    deleting,
    loading: loading && !loadedIds.includes(priorityCampaign.id),
    priorityCampaign,
    relatedEntity,
    updating,
  }
}

export type UsePriorityCampaignPayload = ReturnType<typeof usePriorityCampaign>

export default usePriorityCampaign
